import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "../../Form/TextField";
import Select from "../../Form/Select";
import ColorField from "../../Form/ColorField";
import Button from "../../Buttons/Button";
import FontFamilySelect from "../../../features/creativeTemplates/editor/FontFamilySelect";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_ALIGN,
  TEMPLATES_ALIGN_CENTER,
  TEMPLATES_ALIGN_LEFT,
  TEMPLATES_ALIGN_RIGHT,
  TEMPLATES_CHANGE_ASSET,
  TEMPLATES_FIXED_VALUE,
  TEMPLATES_FONT_COLOR,
  TEMPLATES_FONT_SIZE,
  TEMPLATES_LAYER_NAME,
  TEMPLATES_NO_SELECTION,
  TEMPLATES_ORIENTATION,
  TEMPLATES_ORIENTATION_HORIZONTAL,
  TEMPLATES_ORIENTATION_VERTICAL,
  TEMPLATES_POS_H,
  TEMPLATES_POS_W,
  TEMPLATES_POS_X,
  TEMPLATES_POS_Y,
  TEMPLATES_POSITION,
  TEMPLATES_PROPERTIES,
  TEMPLATES_SELECT_ASSET,
  TEMPLATES_SHAPE_FILL,
  TEMPLATES_SHAPE_RADIUS,
  TEMPLATES_SIZE,
} from "../../../i18n/keysTranslations";

const optionalRules = { required: false };

const emptyInspector = {
  layerName: "",
  fixedValue: "",
  fontAssetRef: "",
  fontSize: "",
  color: "",
  align: "left",
  orientation: "horizontal",
  fill: "",
  radius: "",
  posX: "",
  posY: "",
  posW: "",
  posH: "",
};

const inspectorFromItem = ({ item }) => {
  if (!item) return emptyInspector;
  const node = item.node;
  const box = item.box || {};
  return {
    ...emptyInspector,
    layerName: node.name || "",
    fixedValue: node.source?.value || "",
    fontAssetRef: node.style?.fontAssetRef || "",
    fontSize: node.style?.fontSize ?? "",
    color: node.style?.color || "",
    align: node.style?.align || "left",
    orientation: node.style?.orientation || "horizontal",
    fill: node.shape?.fill || "",
    radius: node.shape?.radius ?? "",
    posX: Math.round(box.x || 0),
    posY: Math.round(box.y || 0),
    posW: Math.round(box.width || 0),
    posH: Math.round(box.height || 0),
  };
};

const FieldGroup = ({ title, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 500, color: "text.secondary", fontSize: "13px" }}
    >
      {title}
    </Typography>
    <Box sx={{ display: "flex", gap: 1 }}>{children}</Box>
  </Box>
);

const InstancePropertiesPanel = ({
  selectedItem,
  isUploadingAsset = false,
  onChangeField,
  onChangeBox,
  onUploadAsset,
}) => {
  const { t } = useTranslationApp();
  const assetFileInputRef = useRef(null);
  const node = selectedItem?.node;
  const isBackground = Boolean(selectedItem?.isBackground);
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: emptyInspector,
  });

  useEffect(() => {
    reset(inspectorFromItem({ item: selectedItem }));
  }, [reset, selectedItem]);

  if (!selectedItem) {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          overflowY: "auto",
          px: 2,
          py: 2.5,
        }}
      >
        <Typography sx={{ fontWeight: 600, mb: 1 }}>
          {t(TEMPLATES_PROPERTIES)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(TEMPLATES_NO_SELECTION)}
        </Typography>
      </Box>
    );
  }

  const type = node.type;

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        px: 2,
        py: 2.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Typography sx={{ fontWeight: 500 }}>{t(TEMPLATES_PROPERTIES)}</Typography>
      <TextField
        control={control}
        errors={errors}
        name="layerName"
        label={t(TEMPLATES_LAYER_NAME)}
        variant="filled"
        fullWidth
        rules={optionalRules}
        actionsChange={(value) => onChangeField({ key: "name", value })}
      />
      {type === "text" ? (
        <>
          <TextField
            control={control}
            errors={errors}
            name="fixedValue"
            label={t(TEMPLATES_FIXED_VALUE)}
            variant="filled"
            fullWidth
            multiline
            minRows={2}
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({ key: "fixedValue", value })
            }
          />
          <FontFamilySelect
            control={control}
            errors={errors}
            onChangeField={onChangeField}
          />
          <TextField
            control={control}
            errors={errors}
            name="fontSize"
            label={t(TEMPLATES_FONT_SIZE)}
            variant="filled"
            type="number"
            fullWidth
            rules={optionalRules}
            InputLabelProps={{ shrink: true }}
            actionsChange={(value) =>
              onChangeField({
                key: "fontSize",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
          <ColorField
            control={control}
            errors={errors}
            name="color"
            label={t(TEMPLATES_FONT_COLOR)}
            fullWidth
            rules={optionalRules}
            actionsChange={(value) => onChangeField({ key: "color", value })}
          />
          <Select
            control={control}
            errors={errors}
            name="align"
            label={t(TEMPLATES_ALIGN)}
            variant="filled"
            fullWidth
            rules={optionalRules}
            actionsChange={(value) => onChangeField({ key: "align", value })}
            options={[
              { value: "left", label: t(TEMPLATES_ALIGN_LEFT) },
              { value: "center", label: t(TEMPLATES_ALIGN_CENTER) },
              { value: "right", label: t(TEMPLATES_ALIGN_RIGHT) },
            ]}
          />
          <Select
            control={control}
            errors={errors}
            name="orientation"
            label={t(TEMPLATES_ORIENTATION)}
            variant="filled"
            fullWidth
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({ key: "orientation", value })
            }
            options={[
              {
                value: "horizontal",
                label: t(TEMPLATES_ORIENTATION_HORIZONTAL),
              },
              {
                value: "vertical",
                label: t(TEMPLATES_ORIENTATION_VERTICAL),
              },
            ]}
          />
        </>
      ) : null}
      {type === "image" && !isBackground ? (
        <>
          {selectedItem.url ? (
            <Box
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                borderRadius: 2,
                border: "1px solid #E4E4E7",
                bgcolor: "action.hover",
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={selectedItem.url}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </Box>
          ) : null}
          <input
            ref={assetFileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file || !onUploadAsset) return;
              onUploadAsset({ file });
            }}
          />
          <Button
            color="secondary"
            loading={isUploadingAsset}
            onClick={() => assetFileInputRef.current?.click()}
          >
            {t(selectedItem.url ? TEMPLATES_CHANGE_ASSET : TEMPLATES_SELECT_ASSET)}
          </Button>
        </>
      ) : null}
      {type === "shape" ? (
        <>
          <ColorField
            control={control}
            errors={errors}
            name="fill"
            label={t(TEMPLATES_SHAPE_FILL)}
            fullWidth
            rules={optionalRules}
            actionsChange={(value) => onChangeField({ key: "fill", value })}
          />
          <TextField
            control={control}
            errors={errors}
            name="radius"
            label={t(TEMPLATES_SHAPE_RADIUS)}
            variant="filled"
            type="number"
            fullWidth
            rules={optionalRules}
            actionsChange={(value) =>
              onChangeField({
                key: "radius",
                value: value === "" ? undefined : Number(value),
              })
            }
          />
        </>
      ) : null}
      <FieldGroup title={t(TEMPLATES_POSITION)}>
        <TextField
          control={control}
          errors={errors}
          name="posX"
          label={t(TEMPLATES_POS_X)}
          variant="filled"
          type="number"
          fullWidth
          disabled={isBackground}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeBox({
              x: value === "" ? 0 : Number(value),
            })
          }
        />
        <TextField
          control={control}
          errors={errors}
          name="posY"
          label={t(TEMPLATES_POS_Y)}
          variant="filled"
          type="number"
          fullWidth
          disabled={isBackground}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeBox({
              y: value === "" ? 0 : Number(value),
            })
          }
        />
      </FieldGroup>
      <FieldGroup title={t(TEMPLATES_SIZE)}>
        <TextField
          control={control}
          errors={errors}
          name="posW"
          label={t(TEMPLATES_POS_W)}
          variant="filled"
          type="number"
          fullWidth
          disabled={isBackground}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeBox({
              width: value === "" ? 0 : Number(value),
            })
          }
        />
        <TextField
          control={control}
          errors={errors}
          name="posH"
          label={t(TEMPLATES_POS_H)}
          variant="filled"
          type="number"
          fullWidth
          disabled={isBackground}
          rules={optionalRules}
          InputLabelProps={{ shrink: true }}
          actionsChange={(value) =>
            onChangeBox({
              height: value === "" ? 0 : Number(value),
            })
          }
        />
      </FieldGroup>
    </Box>
  );
};

export default InstancePropertiesPanel;
